import EventEmitter from "eventemitter2";
import { debounce } from "throttle-debounce";

class Viewport extends EventEmitter {
    constructor({ sizer = null, selector = "html" } = {}) {
        super();

        this.scrollWidth = 0;

        this.width = 1;
        this.height = 1;

        this.minWidth = 0;
        this.minHeight = 0;

        this.viewportWidth = 0;
        this.viewportHeight = 0;

        this.widthOnly = false;

        this.orientation = null;

        this._medias = new Map();
        this.medias = {};

        this.onResize = this.onResize.bind(this);
        this.mediaDisabled = this.mediaDisabled.bind(this);
        this.mediaEnabled = this.mediaEnabled.bind(this);

        if (typeof window == "undefined") return;

        this.sizer = sizer ? sizer : window;
        this.element = document.querySelector(selector);

        this.attach();
        this.resize();
    }

    attach() {
        if (typeof window == "undefined") return;
        window.addEventListener("resize", this.onResize);
        window.addEventListener("orientationchange", this.onResize);

        this.scrollWidth = this.getScrollWidth();
    }

    detach() {
        if (typeof window == "undefined") return;
        window.removeEventListener("resize", this.onResize);
        window.removeEventListener("orientationchange", this.onResize);
    }

    onResize(e) {
        this.computeResize();
    }

    async resize() {
        await this.computeResize(true);
    }

    async computeResize(force = false) {
        if (typeof window == "undefined") return;

        let previousWidth = this.width;
        let previousHeight = this.height;

        // Compute values

        if (this.sizer == window) {
            this.innerWidth = document.documentElement.clientWidth;
            this.outerWidth = window.innerWidth;
            this.innerHeight = document.documentElement.clientHeight;
            this.outerHeight = window.innerHeight;
        } else {
            this.innerWidth = this.outerWidth = this.sizer.offsetWidth;
            this.innerHeight = this.outerHeight = this.sizer.offsetHeight;
        }

        this.viewportWidth = Math.min(this.innerWidth, this.outerWidth);
        this.viewportHeight = Math.min(this.innerHeight, this.outerHeight);

        this.width =
            this.viewportWidth < this.minWidth
                ? this.minWidth
                : this.viewportWidth;
        this.height =
            this.viewportHeight < this.minHeight
                ? this.minHeight
                : this.viewportHeight;

        this.orientation = this.width < this.height ? "portrait" : "landscape";

        // Update medias
        var mediaChanged = Array.from(this._medias.values()).filter(media =>
            media.update()
        );

        // Trigger event
        if (
            (!this.widthOnly &&
                ((Math.abs(previousWidth - this.width) != this.scrollWidth &&
                    previousWidth != this.width) ||
                    previousHeight != this.height)) ||
            (this.widthOnly && previousWidth != this.width)
        ) {
            this.computeResize();
            await this.emitAsync("pre-resize");
            await this.emitAsync("resize");
            await this.emitAsync("post-resize");
            this.computeResize();
        }

        if (mediaChanged.length)
            this.emit(
                "media",
                mediaChanged.map(media => media.name),
                mediaChanged
            );
    }

    add(options) {
        options.viewport = this;
        let media = new ViewportMedia(options);

        media.on("disable", this.mediaDisabled);
        media.on("enable", this.mediaEnabled);

        this._medias.set(media.name, media);
        this.medias[media.name] = false;

        media.update();

        return media;
    }

    remove(media) {
        delete this.medias[media.name];
        this._medias.delete(media.name);
        media.off("disable", this.mediaDisabled);
        media.off("enable", this.mediaEnabled);
    }

    get(name) {
        return this._medias.get(name);
    }

    mediaDisabled(media) {
        this.medias[media.name] = false;
        if (media.addClass)
            this.element.classList.remove(media.classPrefix + media.name);
    }
    mediaEnabled(media) {
        this.medias[media.name] = true;
        if (media.addClass)
            this.element.classList.add(media.classPrefix + media.name);
    }

    getScrollWidth() {
        const tempElement = document.createElement("div");
        tempElement.style.width = "100px";
        tempElement.style.height = "100px";
        tempElement.style.overflow = "scroll";
        tempElement.style.position = "absolute";
        tempElement.style.top = "-9999px";
        document.body.appendChild(tempElement);
        const scrollWidth = tempElement.offsetWidth - tempElement.clientWidth;
        document.body.removeChild(tempElement);
        return scrollWidth;
    }
}

class ViewportMedia extends EventEmitter {
    constructor({
        viewport = new Viewport(),
        name = "",
        minWidth = -Infinity,
        minHeight = -Infinity,
        maxWidth = +Infinity,
        maxHeight = +Infinity,
        addClass = true,
        classPrefix = "",
        condition = null,
        meta = null,
        media = null
    } = {}) {
        super();

        this.viewport = viewport;
        this.name = name;
        this.minWidth = minWidth;
        this.minHeight = minHeight;
        this.maxWidth = maxWidth;
        this.maxHeight = maxHeight;
        this.addClass = addClass;
        this.classPrefix = classPrefix;
        this.condition = condition;
        this.meta = meta;
        this.media = media;

        this.active = false;
    }

    update() {
        if (typeof window == "undefined") return false;

        var active =
            this.viewport.width >= this.minWidth &&
            this.viewport.width < this.maxWidth &&
            this.viewport.height >= this.minHeight &&
            this.viewport.height < this.maxHeight &&
            (this.media ? window.matchMedia(this.media).matches : true);
        if (typeof this.condition == "function")
            active = this.condition(active);

        if (this.active && !active) {
            this.active = false;
            this.emit("disable", this);
            return true;
        } else if (!this.active && active) {
            this.active = true;
            this.emit("enable", this);
            return true;
        }
        return false;
    }
}

export default Viewport;
export { Viewport, ViewportMedia };
